import { gql, useMutation } from "@apollo/client";
import { useCallback } from "react";
import { CallbackFailure, CallbackSuccess } from "../../types";
import {
  GetPresignedUrlMutation,
  MutationGetPresignedUrlArgs,
} from "../../generated/graphql";

const GET_PRESIGNED_URL_MUTATION_GQL = gql`
  mutation GetPresignedUrl(
    $appointmentShortId: String!
    $uploadMeta: UploadMeta
  ) {
    getPresignedUrl(
      appointmentShortId: $appointmentShortId
      uploadMeta: $uploadMeta
    ) {
      url
    }
  }
`;

export function useGetPresignedUrlMutation() {
  return useMutation<GetPresignedUrlMutation, MutationGetPresignedUrlArgs>(
    GET_PRESIGNED_URL_MUTATION_GQL,
  );
}

type GetPresignedUrlCallbackResponse =
  | CallbackSuccess<GetPresignedUrlMutation>
  | CallbackFailure<Error>;

export function useGetPresignedUrlCallback() {
  const [getPresignedUrl] = useGetPresignedUrlMutation();

  return useCallback(
    async (
      appointmentShortId: MutationGetPresignedUrlArgs["appointmentShortId"],
      uploadMeta?: MutationGetPresignedUrlArgs["uploadMeta"],
    ): Promise<GetPresignedUrlCallbackResponse> => {
      try {
        const data = (
          await getPresignedUrl({
            variables: { appointmentShortId, uploadMeta },
          })
        )?.data;
        if (!data) {
          throw new Error(
            "Failed to fetch presigned URL; missing data in response",
          );
        }
        return {
          status: "success",
          value: data,
        };
      } catch (error) {
        return {
          status: "failure",
          value: error as Error,
        };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
}
